/**
 * teamStore.js — Singleton module-level store for team members.
 *
 * This store is the SINGLE source of truth shared by:
 *   • Settings → Team  (`TeamsManager.js`)
 *   • Settings → Roles (`RolesPermissionsTab.js`)
 *   • Any downstream consumer that needs "who's on the team right now"
 *
 * It guarantees both tabs render identical data because they subscribe to
 * the exact same in-memory array, produced by a single `listMembers` call.
 *
 * Public surface:
 *   subscribe(cb)   — pub/sub for React's useSyncExternalStore
 *   getSnapshot()   — current store state { members, loading, error, hydrated }
 *   fetch()         — (re)hydrate from `/api/team/members`
 *   setRole(id, roleId)
 *   setSchedulable(id, bool)
 *   linkNpi(id, npi)
 *   unlinkNpi(id)
 *   updateMember(id, patch)
 *   createMember(payload)
 *   deleteMember(id)
 *   seedDemo(members)  — demo-mode loader (bypasses backend)
 *   clear()
 *
 * All mutation methods:
 *   1. patch the local members array OPTIMISTICALLY,
 *   2. notify subscribers (both tabs render the same array instantly),
 *   3. call the vendor endpoint via `teamApi`,
 *   4. on failure → revert to the pre-mutation snapshot and notify.
 */
import * as teamApi from './api/teamApi';
import { normalizeMembers, normalizeMember } from './api/teamAdapter';

const listeners = new Set();

// IMPORTANT: `useSyncExternalStore` compares snapshots with `Object.is`.
// We must return a NEW snapshot object on every state change or subscribed
// components will not re-render even after `notify()`. Mutate the internal
// `state` for bookkeeping, then rebuild `snapshot` before each notify.
const state = {
  members: [],
  loading: false,
  error: null,
  hydrated: false,
  mode: 'real',  // 'real' | 'demo'
  _inFlight: null,
};

let snapshot = {
  members: state.members,
  loading: state.loading,
  error: state.error,
  hydrated: state.hydrated,
};

const rebuildSnapshot = () => {
  snapshot = {
    members: state.members,
    loading: state.loading,
    error: state.error,
    hydrated: state.hydrated,
  };
};

const notify = () => {
  rebuildSnapshot();
  for (const l of listeners) {
    try { l(); } catch { /* noop */ }
  }
};

const replaceMember = (id, patch) => {
  state.members = state.members.map((m) => (m.id === id ? normalizeMember({ ...m, ...patch }) : m));
};

export const teamStore = {
  subscribe(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },

  getSnapshot() {
    return snapshot;
  },

  setMode(mode) {
    if (state.mode === mode) return;
    state.mode = mode;
    state.hydrated = false;
    state.members = [];
    state.error = null;
    notify();
  },

  seedDemo(rawMembers) {
    state.mode = 'demo';
    state.members = normalizeMembers(rawMembers);
    state.loading = false;
    state.error = null;
    state.hydrated = true;
    notify();
  },

  async fetch(force = false) {
    if (state.mode === 'demo') return;
    if (state.loading || (state.hydrated && !force)) return;
    if (state._inFlight) return state._inFlight;

    state.loading = true;
    state.error = null;
    notify();

    const p = teamApi.listMembers().then((res) => {
      state.loading = false;
      state._inFlight = null;
      if (res.ok) {
        state.members = normalizeMembers(res.data?.members || []);
        state.hydrated = true;
        state.error = null;
      } else {
        state.error = res.error || { message: 'Failed to load team members' };
      }
      notify();
    });
    state._inFlight = p;
    return p;
  },

  async setRole(memberId, roleId) {
    if (!memberId || !roleId) return { ok: false, error: { message: 'memberId and roleId required' } };
    const before = state.members;
    // Optimistic: reflect the role change (and derived category) locally.
    replaceMember(memberId, { role: roleId, category: undefined, user_type: undefined });
    notify();

    if (state.mode === 'demo') {
      return { ok: true, data: { member: state.members.find((m) => m.id === memberId) } };
    }
    const res = await teamApi.setRole(memberId, roleId);
    if (!res.ok) {
      state.members = before;  // revert
      state.error = res.error;
      notify();
      return res;
    }
    const updated = res.data?.member || null;
    if (updated) replaceMember(memberId, updated);
    notify();
    return res;
  },

  async setSchedulable(memberId, schedulable) {
    const before = state.members;
    replaceMember(memberId, { is_schedulable: !!schedulable, schedulable: !!schedulable });
    notify();
    if (state.mode === 'demo') return { ok: true };
    const res = await teamApi.setSchedulable(memberId, !!schedulable);
    if (!res.ok) {
      state.members = before;
      notify();
    } else if (res.data?.member) {
      replaceMember(memberId, res.data.member);
      notify();
    }
    return res;
  },

  async linkNpi(memberId, npi) {
    const before = state.members;
    replaceMember(memberId, { npi });
    notify();
    if (state.mode === 'demo') return { ok: true };
    const res = await teamApi.linkNpi(memberId, npi);
    if (!res.ok) {
      state.members = before;
      notify();
    } else if (res.data?.member) {
      replaceMember(memberId, res.data.member);
      notify();
    }
    return res;
  },

  async unlinkNpi(memberId) {
    const before = state.members;
    replaceMember(memberId, { npi: null });
    notify();
    if (state.mode === 'demo') return { ok: true };
    const res = await teamApi.unlinkNpi(memberId);
    if (!res.ok) {
      state.members = before;
      notify();
    } else if (res.data?.member) {
      replaceMember(memberId, res.data.member);
      notify();
    }
    return res;
  },

  async updateMember(memberId, patch) {
    const before = state.members;
    replaceMember(memberId, patch);
    notify();
    if (state.mode === 'demo') return { ok: true };
    const res = await teamApi.updateMember(memberId, patch);
    if (!res.ok) {
      state.members = before;
      notify();
    } else if (res.data?.member) {
      replaceMember(memberId, res.data.member);
      notify();
    }
    return res;
  },

  async createMember(payload) {
    if (state.mode === 'demo') {
      const id = `m_demo_${Date.now().toString(36)}`;
      const m = normalizeMember({ ...payload, id });
      state.members = [...state.members, m];
      notify();
      return { ok: true, data: { member: m } };
    }
    const res = await teamApi.createMember(payload);
    if (res.ok && res.data?.member) {
      state.members = [...state.members, normalizeMember(res.data.member)];
      notify();
    }
    return res;
  },

  async deleteMember(memberId) {
    const before = state.members;
    state.members = state.members.filter((m) => m.id !== memberId);
    notify();
    if (state.mode === 'demo') return { ok: true };
    const res = await teamApi.deleteMember(memberId);
    if (!res.ok) {
      state.members = before;
      notify();
    }
    return res;
  },

  clear() {
    state.members = [];
    state.loading = false;
    state.error = null;
    state.hydrated = false;
    state._inFlight = null;
    notify();
  },
};

export default teamStore;
